<template>
  <div
    class="stock-invoice-hero"
    :class="[
      `stock-invoice-hero--${direction || 'outgoing'}`,
      { 'stock-invoice-hero--new': isNew },
    ]"
  >
    <div class="stock-invoice-hero__pattern" aria-hidden="true"></div>
    <div class="stock-invoice-hero__content">
      <div class="stock-invoice-hero__main">
        <div class="stock-invoice-hero__code-row">
          <div class="stock-invoice-hero__icon-wrap">
            <v-icon large color="white">mdi-file-document-outline</v-icon>
          </div>
          <div>
            <div class="stock-invoice-hero__code">{{ code || $t("STOCK.INVOICES.NEW") }}</div>
            <div v-if="fiscalNumber && internalCode && fiscalNumber !== internalCode" class="stock-invoice-hero__internal caption">
              {{ $t("STOCK.INVOICES.INTERNAL_REF") }}: {{ internalCode }}
            </div>
            <div v-if="companyName" class="stock-invoice-hero__company">{{ companyName }}</div>
            <div v-else-if="isNew" class="stock-invoice-hero__company stock-invoice-hero__company--muted">
              {{ $t("STOCK.INVOICES.NEW_HINT") }}
            </div>
          </div>
        </div>
        <div class="stock-invoice-hero__chips">
          <v-chip v-if="status" small :class="['stock-invoice-hero__chip', `stock-invoice-status--${status}`]" dark>
            {{ statusLabel }}
          </v-chip>
          <v-chip v-if="type" small outlined class="stock-invoice-hero__chip stock-invoice-hero__chip--outline">
            {{ typeLabel }}
          </v-chip>
          <v-chip v-if="direction" small class="stock-invoice-hero__chip stock-invoice-hero__chip--direction" dark>
            <v-icon left x-small>{{ directionIcon }}</v-icon>
            {{ directionLabel }}
          </v-chip>
          <v-chip v-if="isLocked" small color="amber darken-3" dark class="stock-invoice-hero__chip">
            <v-icon left x-small>mdi-lock</v-icon>
            {{ $t("STOCK.INVOICES.LOCKED") }}
          </v-chip>
        </div>
        <div v-if="showExternalInMeta || issueDate" class="stock-invoice-hero__meta">
          <span v-if="showExternalInMeta">{{ $t("STOCK.INVOICES.EXTERNAL_NUMBER") }}: {{ externalNumber }}</span>
          <span v-if="showExternalInMeta && issueDate" class="mx-2">·</span>
          <span v-if="issueDate">{{ $t("STOCK.INVOICES.ISSUE_DATE") }}: {{ issueDate }}</span>
        </div>
      </div>
      <div v-if="$scopedSlots.actions" class="stock-invoice-hero__actions">
        <slot name="actions"></slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "StockInvoiceHero",
  props: {
    code: { type: String, default: "" },
    internalCode: { type: String, default: "" },
    fiscalNumber: { type: String, default: "" },
    status: { type: String, default: "" },
    type: { type: String, default: "" },
    direction: { type: String, default: "" },
    companyName: { type: String, default: "" },
    externalNumber: { type: String, default: "" },
    issueDate: { type: String, default: "" },
    isLocked: { type: Boolean, default: false },
    isNew: { type: Boolean, default: false },
  },
  computed: {
    statusLabel() {
      const key = `STOCK.STATUSES.INVOICE.${(this.status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : this.status;
    },
    typeLabel() {
      const key = `STOCK.INVOICES.TYPES.${(this.type || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : this.type;
    },
    directionLabel() {
      const key = `STOCK.INVOICES.DIRECTIONS.${(this.direction || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : this.direction;
    },
    directionIcon() {
      return this.direction === "outgoing" ? "mdi-arrow-right-bold" : "mdi-arrow-left-bold";
    },
    showExternalInMeta() {
      return !!this.externalNumber && this.externalNumber !== this.code;
    },
  },
};
</script>

<style scoped>
.stock-invoice-hero {
  position: relative;
  border-radius: 16px;
  padding: 28px 32px;
  margin-bottom: 20px;
  overflow: hidden;
  color: #fff;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}
.stock-invoice-hero--outgoing {
  background: linear-gradient(135deg, #00695c 0%, #00897b 45%, #26a69a 100%);
}
.stock-invoice-hero--incoming {
  background: linear-gradient(135deg, #4527a0 0%, #5e35b1 45%, #7e57c2 100%);
}
.stock-invoice-hero--new {
  box-shadow: 0 8px 32px rgba(0, 105, 92, 0.25);
}
.stock-invoice-hero__pattern {
  position: absolute;
  inset: 0;
  opacity: 0.08;
  background-image: radial-gradient(circle at 20% 80%, #fff 1px, transparent 1px),
    radial-gradient(circle at 80% 20%, #fff 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
}
.stock-invoice-hero__content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}
.stock-invoice-hero__main {
  min-width: 0;
  flex: 1 1 auto;
}
.stock-invoice-hero__actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex: 0 0 auto;
  max-width: 460px;
}
.stock-invoice-hero__actions >>> .stock-invoice-hero__action-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}
@media (max-width: 960px) {
  .stock-invoice-hero__content {
    flex-direction: column;
  }
  .stock-invoice-hero__actions {
    max-width: none;
    align-items: stretch;
    width: 100%;
  }
  .stock-invoice-hero__actions >>> .stock-invoice-hero__action-row {
    justify-content: flex-start;
  }
}
.stock-invoice-hero__code {
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}
.stock-invoice-hero__internal {
  opacity: 0.8;
  margin-top: 2px;
}
.stock-invoice-hero__company {
  font-size: 1.05rem;
  font-weight: 500;
  opacity: 0.92;
  margin-top: 4px;
}
.stock-invoice-hero__company--muted {
  opacity: 0.75;
  font-style: italic;
}
.stock-invoice-hero__code-row {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}
.stock-invoice-hero__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  margin-right: 16px;
  flex-shrink: 0;
}
.stock-invoice-hero__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}
.stock-invoice-hero__chip--outline {
  border-color: rgba(255, 255, 255, 0.7) !important;
  color: #fff !important;
}
.stock-invoice-hero__chip--direction {
  background: rgba(0, 0, 0, 0.2) !important;
}
.stock-invoice-hero__meta {
  font-size: 0.875rem;
  opacity: 0.85;
}
</style>
